// How each element looks on the canvas (world coordinates; the world layer applies the camera).
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type {
  CardElement, FontKey, FrameElement, ImageElement, LineElement, NoteElement, SepiaElement, ShapeElement, StickerElement, TextElement
} from "../../../shared/model";
import { inkElementPath, PENS } from "../ink";
import { linePolyline, sketchLine, sketchShape } from "../sketch";
import { adaptInk, FONTS, PAPERS } from "../style";
import { measureText } from "../measure";
import { loadKatex, needsMath, onKatexReady, renderMarkdown } from "../markdown";
import { useEditor, useElement, useUi } from "./hooks";
import { stickerUrl } from "../../data/stickers";

const deg = (r: number) => (r * 180) / Math.PI;

function boxStyle(el: SepiaElement, extra: CSSProperties = {}): CSSProperties {
  return {
    transform: `translate(${el.x}px, ${el.y}px)${el.rot ? ` rotate(${deg(el.rot)}deg)` : ""}`,
    width: el.w,
    height: el.h,
    opacity: el.opacity ?? 1,
    ...extra
  };
}

function fontStyle(font: FontKey, size: number): CSSProperties {
  const f = FONTS[font];
  return { fontFamily: f.family, fontSize: size * f.scale, lineHeight: f.lineHeight };
}

// ---------- ink ----------

export const InkStroke = memo(function InkStroke({ id, darkPaper }: { id: string; darkPaper: boolean }) {
  const el = useElement(id);
  if (!el || el.type !== "ink") return null;
  const preset = PENS[el.pen] ?? PENS.fountain;
  const style: CSSProperties | undefined = preset.blend && !darkPaper ? { mixBlendMode: "multiply" } : undefined;
  return (
    <path
      d={inkElementPath(el)}
      fill={preset.blend ? el.color : adaptInk(el.color, darkPaper)}
      opacity={preset.opacity * (el.opacity ?? 1) * (preset.blend && darkPaper ? 1.25 : 1)}
      transform={`translate(${el.x} ${el.y})${el.rot ? ` rotate(${deg(el.rot)} ${el.w / 2} ${el.h / 2})` : ""}`}
      style={style}
    />
  );
});

export const InkRun = memo(function InkRun({ ids }: { ids: string[] }) {
  const dark = useUi((_, ed) => !!PAPERS[ed.bg.paper]?.dark);
  return (
    <svg className="ink-run" width="1" height="1">
      {ids.map((id) => (
        <InkStroke key={id} id={id} darkPaper={dark} />
      ))}
    </svg>
  );
});

// ---------- in-place text editing ----------

function useAutoFocus(editing: boolean, ref: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>, selectAll = false) {
  useLayoutEffect(() => {
    if (!editing || !ref.current) return;
    const node = ref.current;
    node.focus({ preventScroll: true });
    const len = node.value.length;
    if (selectAll) node.select();
    else node.setSelectionRange(len, len);
    // keep the box above the iPad keyboard
    const vv = window.visualViewport;
    const t = window.setTimeout(() => {
      if (!vv) return;
      const r = node.getBoundingClientRect();
      const bottomLimit = vv.height + vv.offsetTop - 16;
      if (r.bottom > bottomLimit) window.dispatchEvent(new CustomEvent("sepia:pan", { detail: { dy: bottomLimit - r.bottom } }));
    }, 350);
    return () => window.clearTimeout(t);
  }, [editing, ref, selectAll]);
}

function EditArea({ value, onChange, style, className, placeholder, selectAll }: { value: string; onChange: (v: string) => void; style?: CSSProperties; className?: string; placeholder?: string; selectAll?: boolean }) {
  const ed = useEditor();
  const ref = useRef<HTMLTextAreaElement>(null);
  useAutoFocus(true, ref, selectAll);
  useLayoutEffect(() => {
    // letters typed fast, before this box took the focus from the hidden keyboard field, belong here
    const proxy = document.querySelector<HTMLTextAreaElement>(".focus-proxy");
    if (!proxy?.value) return;
    const early = proxy.value;
    proxy.value = "";
    onChange((selectAll ? "" : value) + early);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => {
    // grow with the text (CSS field-sizing isn't available in Safari)
    const t = ref.current;
    if (!t || className?.includes("note-edit") || className?.includes("card-edit")) return;
    t.style.height = "auto";
    t.style.height = t.scrollHeight + "px";
  }, [value, className]);
  return (
    <textarea
      ref={ref}
      className={"edit-area " + (className ?? "")}
      value={value}
      placeholder={placeholder}
      spellCheck
      style={style}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape" || (e.key === "Enter" && (e.metaKey || e.ctrlKey))) {
          e.preventDefault();
          ed.stopEditing();
        }
      }}
      onBlur={() => {
        // leaving the field (keyboard dismissed, tapped a toolbar) ends editing
        window.setTimeout(() => {
          if (ed.ui.editingId && document.activeElement?.tagName !== "TEXTAREA" && document.activeElement?.tagName !== "INPUT") ed.stopEditing();
        }, 0);
      }}
    />
  );
}

// ---------- text ----------

export const TextView = memo(function TextView({ id }: { id: string }) {
  const ed = useEditor();
  const el = useElement(id) as TextElement | undefined;
  const editing = useUi((ui) => ui.editingId === id);
  const dark = useUi((_, e) => !!PAPERS[e.bg.paper]?.dark);
  if (!el) return null;
  const fs = fontStyle(el.font, el.fontSize);
  const color = adaptInk(el.color, dark);
  const style = boxStyle(el, {
    ...fs,
    color,
    textAlign: el.align,
    fontWeight: el.bold ? 700 : undefined,
    fontStyle: el.italic ? "italic" : undefined,
    height: "auto",
    width: el.autoW ? "max-content" : el.w,
    minWidth: 12
  });
  const update = (text: string) =>
    ed.update(id, (cur) => {
      const t = cur as TextElement;
      const m = measureText(text, t.font, t.fontSize, { maxWidth: t.autoW ? undefined : t.w, bold: t.bold, italic: t.italic });
      return { ...t, text, w: t.autoW ? Math.max(20, m.w) : t.w, h: Math.max(m.h, t.fontSize) };
    }, "type-" + id);
  return (
    <div className="el el-text" style={style} data-id={id}>
      {editing ? (
        <EditArea value={el.text} onChange={update} placeholder="Type something…" style={{ ...fs, color, textAlign: el.align, width: el.autoW ? Math.max(el.w + 40, 120) : el.w, minHeight: el.h }} />
      ) : (
        el.text || "​"
      )}
    </div>
  );
});

// ---------- sticky note ----------

function Tape({ color, width = "38%", rotate = -3, top = -14 }: { color: string; width?: string; rotate?: number; top?: number }) {
  return <span className="tape" style={{ background: color, width, transform: `translateX(-50%) rotate(${rotate}deg)`, top }} />;
}

export const NoteView = memo(function NoteView({ id }: { id: string }) {
  const ed = useEditor();
  const el = useElement(id) as NoteElement | undefined;
  const editing = useUi((ui) => ui.editingId === id);
  const textRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(1);
  const text = el?.text ?? "";
  const size = el?.fontSize ?? 30;
  const w = el?.w ?? 200;
  const h = el?.h ?? 200;
  // shrink long notes to fit, like a real sticky note filled edge to edge
  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node || editing) return;
    let k = 1;
    node.style.setProperty("--fit", "1");
    for (let i = 0; i < 10 && node.scrollHeight > node.clientHeight + 2 && k > 0.4; i++) {
      k *= 0.88;
      node.style.setProperty("--fit", String(k));
    }
    setFit(k);
  }, [text, size, w, h, editing]);
  if (!el) return null;
  const fs = fontStyle(el.font, el.fontSize);
  return (
    <div className={"el el-note" + (el.shadow === false ? "" : " lifted")} style={boxStyle(el, { ["--paper" as string]: el.paper })} data-id={id}>
      <div className="note-paper" />
      {el.tape && <Tape color={el.tape} />}
      {editing ? (
        <EditArea
          className="note-edit"
          value={el.text}
          onChange={(t) => ed.update(id, (cur) => ({ ...(cur as NoteElement), text: t }), "type-" + id)}
          placeholder="Jot it down…"
          style={{ ...fs, color: el.textColor, textAlign: el.align }}
        />
      ) : (
        <div ref={textRef} className="note-text" style={{ ...fs, fontSize: `calc(${(fs.fontSize as number)}px * var(--fit, ${fit}))`, color: el.textColor, textAlign: el.align }}>
          {el.text}
        </div>
      )}
    </div>
  );
});

// ---------- index card (markdown) ----------

export const CardView = memo(function CardView({ id }: { id: string }) {
  const ed = useEditor();
  const el = useElement(id) as CardElement | undefined;
  const editing = useUi((ui) => ui.editingId === id);
  const [katexTick, setKatexTick] = useState(0);
  const md = el?.md ?? "";
  const math = needsMath(md);
  useEffect(() => {
    if (!math) return;
    const off = onKatexReady(() => setKatexTick((n) => n + 1));
    loadKatex();
    return () => {
      off();
    };
  }, [math]);
  // katexTick re-renders once the math renderer has loaded
  const html = useMemo(() => renderMarkdown(md), [md, katexTick]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  useLayoutEffect(() => {
    const n = bodyRef.current;
    if (n) setOverflow(n.scrollHeight > n.clientHeight + 4);
  }, [html, el?.h, el?.w]);
  if (!el) return null;
  return (
    <div className={"el el-card" + (el.shadow === false ? "" : " lifted")} style={boxStyle(el, { ["--paper" as string]: el.paper, ["--accent" as string]: el.accent, fontSize: el.fontSize })} data-id={id}>
      {editing ? (
        <EditArea
          className="card-edit"
          value={el.md}
          onChange={(t) => ed.update(id, (cur) => ({ ...(cur as CardElement), md: t }), "type-" + id)}
          placeholder={"# Title\nWrite in markdown: **bold**, - lists, - [ ] tasks, [[Another board]], $e^{i\\pi}+1=0$"}
        />
      ) : (
        <>
          <div ref={bodyRef} className={"card-body" + (md.trim() ? "" : " empty")} dangerouslySetInnerHTML={{ __html: md.trim() ? html : "<p>Double-tap to write…</p>" }} />
          {overflow && <div className="card-more">more ↓</div>}
        </>
      )}
    </div>
  );
});

// ---------- shape ----------

export const ShapeView = memo(function ShapeView({ id }: { id: string }) {
  const ed = useEditor();
  const el = useElement(id) as ShapeElement | undefined;
  const editing = useUi((ui) => ui.editingId === id);
  const dark = useUi((_, e) => !!PAPERS[e.bg.paper]?.dark);
  if (!el) return null;
  // a solid fill carries its own outline; otherwise the outline sits on the paper and must stay visible
  const paths = sketchShape({ ...el, stroke: el.fillStyle === "solid" ? el.stroke : adaptInk(el.stroke, dark) });
  const fs = fontStyle(el.font, el.fontSize);
  // text sits on the fill when the shape is solid; otherwise it sits on the paper
  const textColor = el.fillStyle === "solid" ? el.textColor : adaptInk(el.textColor, dark);
  return (
    <div className={"el el-shape" + (el.shadow ? " lifted-svg" : "")} style={boxStyle(el)} data-id={id}>
      <svg className="shape-svg" width={el.w} height={el.h} viewBox={`0 0 ${Math.max(2, Math.round(el.w * 2) / 2)} ${Math.max(2, Math.round(el.h * 2) / 2)}`} preserveAspectRatio="none">
        {paths.map((p, i) => (
          <path key={i} d={p.d} stroke={p.stroke} strokeWidth={p.strokeWidth} fill={p.fill} strokeDasharray={p.dash} strokeLinecap="round" strokeLinejoin="round" />
        ))}
      </svg>
      {editing ? (
        <div className="shape-label editing">
          <EditArea value={el.text} onChange={(t) => ed.update(id, (cur) => ({ ...(cur as ShapeElement), text: t }), "type-" + id)} style={{ ...fs, color: textColor, textAlign: el.align }} placeholder="Label" />
        </div>
      ) : (
        el.text && (
          <div className="shape-label" style={{ ...fs, color: textColor, textAlign: el.align, padding: el.kind === "diamond" ? "22% 18%" : el.kind === "triangle" ? "38% 14% 6%" : undefined }}>
            {el.text}
          </div>
        )
      )}
    </div>
  );
});

// ---------- connector ----------

export const LineView = memo(function LineView({ id }: { id: string }) {
  const ed = useEditor();
  const el = useElement(id) as LineElement | undefined;
  const editing = useUi((ui) => ui.editingId === id);
  const dark = useUi((_, e) => !!PAPERS[e.bg.paper]?.dark);
  if (!el) return null;
  const stroke = adaptInk(el.stroke, dark);
  const paths = sketchLine({ ...el, stroke });
  const poly = linePolyline(el);
  const mid = poly[Math.floor(poly.length / 2)] ?? { x: el.x, y: el.y };
  const midPt = poly.length === 2 ? { x: (poly[0].x + poly[1].x) / 2, y: (poly[0].y + poly[1].y) / 2 } : mid;
  const fs = fontStyle(el.font, el.fontSize);
  return (
    <>
      <svg className="el-line" width="1" height="1" style={{ opacity: el.opacity ?? 1 }} data-id={id}>
        {paths.map((p, i) => (
          <path key={i} d={p.d} stroke={p.stroke} strokeWidth={p.strokeWidth} fill={p.fill} strokeDasharray={p.dash} strokeLinecap="round" strokeLinejoin="round" />
        ))}
      </svg>
      {(el.label || editing) && (
        <div className="line-label" style={{ transform: `translate(${midPt.x}px, ${midPt.y}px) translate(-50%, -50%)`, ...fs, color: stroke }}>
          {editing ? (
            <EditArea value={el.label} onChange={(t) => ed.update(id, (cur) => ({ ...(cur as LineElement), label: t }), "type-" + id)} style={{ ...fs, color: stroke, textAlign: "center", width: 160 }} placeholder="label" />
          ) : (
            el.label
          )}
        </div>
      )}
    </>
  );
});

// ---------- image ----------

export const ImageView = memo(function ImageView({ id }: { id: string }) {
  const ed = useEditor();
  const el = useElement(id) as ImageElement | undefined;
  const editing = useUi((ui) => ui.editingId === id);
  const [failed, setFailed] = useState(false);
  if (!el) return null;
  const src = ed.host.resolveImage(el.src);
  return (
    <div className={"el el-image frame-" + el.frame} style={boxStyle(el)} data-id={id}>
      {el.frame === "tape" && (
        <>
          <span className="tape corner-left" style={{ background: "#d0845f" }} />
          <span className="tape corner-right" style={{ background: "#93ad86" }} />
        </>
      )}
      <div className="image-inner">
        {failed ? <div className="image-missing">image unavailable</div> : <img src={src} alt={el.caption || ""} draggable={false} decoding="async" loading="lazy" onError={() => setFailed(true)} />}
      </div>
      {el.frame === "polaroid" && (
        <div className="polaroid-caption">
          {editing ? (
            <input
              className="edit-input"
              value={el.caption ?? ""}
              autoFocus
              placeholder="caption"
              onChange={(e) => ed.update(id, (cur) => ({ ...(cur as ImageElement), caption: e.target.value }), "type-" + id)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter" || e.key === "Escape") ed.stopEditing();
              }}
              onBlur={() => ed.stopEditing()}
            />
          ) : (
            el.caption
          )}
        </div>
      )}
    </div>
  );
});

// ---------- frame ----------

export const FrameView = memo(function FrameView({ id }: { id: string }) {
  const ed = useEditor();
  const el = useElement(id) as FrameElement | undefined;
  const editing = useUi((ui) => ui.editingId === id);
  const inputRef = useRef<HTMLInputElement>(null);
  useAutoFocus(editing, inputRef, true);
  if (!el) return null;
  return (
    <div className="el el-frame" style={boxStyle(el, { ["--frame" as string]: el.color })} data-id={id}>
      <div className="frame-title">
        {editing ? (
          <input
            ref={inputRef}
            className="edit-input"
            value={el.title}
            onChange={(e) => ed.update(id, (cur) => ({ ...(cur as FrameElement), title: e.target.value.slice(0, 80) }), "type-" + id)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter" || e.key === "Escape") ed.stopEditing();
            }}
            onBlur={() => ed.stopEditing()}
          />
        ) : (
          el.title || "Untitled"
        )}
      </div>
    </div>
  );
});

// ---------- sticker ----------

export const StickerView = memo(function StickerView({ id }: { id: string }) {
  const el = useElement(id) as StickerElement | undefined;
  if (!el) return null;
  return (
    <div className="el el-sticker" style={boxStyle(el)} data-id={id}>
      <img src={stickerUrl(el.key)} alt="" draggable={false} />
    </div>
  );
});

export function ElementView({ el }: { el: SepiaElement }) {
  switch (el.type) {
    case "text":
      return <TextView id={el.id} />;
    case "note":
      return <NoteView id={el.id} />;
    case "card":
      return <CardView id={el.id} />;
    case "shape":
      return <ShapeView id={el.id} />;
    case "line":
      return <LineView id={el.id} />;
    case "image":
      return <ImageView id={el.id} />;
    case "frame":
      return <FrameView id={el.id} />;
    case "sticker":
      return <StickerView id={el.id} />;
    default:
      return null;
  }
}
